import { Placeholder } from '@tiptap/extension-placeholder';
import { StarterKit } from '@tiptap/starter-kit';
import React, { FC, useEffect, useRef } from 'react';

import { parseText } from '../../../formatters/editor';
import { useEditor } from '../../../hooks/useEditor';

import EditorControls from './EditorControls';
import { configurations } from './configurations';
import {
  getCustomLink,
  getEditorProps,
  getIsMaxLengthExceeded,
  shouldShowControls,
  validateTextareaMaxLength,
} from './helpers';
import * as Styled from './styled';
import { Props } from './types';

/**
 * tiptap Editor
 *
 * @param props                     Props passed to the component
 * @param props.breakpointMin       Breakpoint at which the component is hidden
 * @param props.className           styled-components generated class name, needed for styling
 * @param props.id                  ID of the text area
 * @param props.isDisabled          Determines if the input is disabled
 * @param props.isInvalid           Determines if the input is invalid
 * @param props.isRequired          Determines if the input is isRequired
 * @param props.maxLength           Maximum number of characters for the text area
 * @param props.minLength           Minimum text length
 * @param props.name                name attribute for the textarea
 * @param props.nodeId              The ID of the node being edited (null for creation)
 * @param props.onBlur              Callback to be invoked when field is blurred
 * @param props.onChangeText        Callback to be invoked when text is changed
 * @param props.placeholder         Text area placeholder
 * @param props.requestEditCancel   Callback which clears draft and exits edit mode
 * @param props.setIsEditorEmpty    Callback which checks is editor empty (if there are only empty spaces and new lines)
 * @param props.shouldDisableSubmit If the submit button should be disabled
 * @param props.shouldFocus         Should the editor be auto focused
 * @param props.usage               What the editor is used for
 * @param props.value               Current text area value
 * @returns                         The component itself
 */
const Editor: FC<Props> = ({
  breakpointMin = null,
  className,
  id,
  isDisabled = false,
  isInvalid = false,
  isRequired = false,
  maxLength,
  minLength,
  name,
  nodeId = null,
  onBlur,
  onChangeText,
  placeholder,
  requestEditCancel,
  setIsEditorEmpty,
  shouldDisableSubmit = false,
  shouldFocus,
  usage,
  value,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  /** We are using ref for 'usage' prop because of onUpdate() function */
  const usageRef = useRef(usage);

  const { buttons, multiline } = configurations[usage];
  const hasButtons = buttons.size !== 0;
  const showControls = shouldShowControls(hasButtons, isDisabled);

  const CustomLink = getCustomLink();
  const editor = useEditor({
    content: parseText(value),
    editable: isDisabled === false,
    editorProps: getEditorProps(isDisabled, isRequired, placeholder, usage),
    extensions: [
      StarterKit,
      CustomLink,
      Placeholder.configure({
        placeholder,
      }),
    ],
    /**
     * Callback for update event on the editor
     *
     * @param params        Params passed to onUpdate function
     * @param params.editor Editor interface
     */
    onUpdate: ({ editor: editorInterface }) => {
      // we consider that editor is empty when:
      // a) there's nothing entered
      // b) when the user types just whitespace characters, unless the editor contains an empty list.
      // Additional notes:
      // i) Whitespace characters include: Spaces ( ), Tabs (\t), New lines (\n),
      // Carriage returns (\r), Vertical tabs (\v), Form feeds (\f).
      // ii) editor text for an empty bullet- or ordered list is '\n\n\n\n'. This is a special
      // case because the editor is switching from paragraph to an empty list after typing e.g. '1. '.
      // iii) editorInterface.getText().trim() gives '' for '1. ' string,
      // because the editor is switching from paragraph to a list which has nothing in it
      // so we stopped using that.

      const editorMarkupForEmptyLists = '\n\n\n\n';
      const isEmpty =
        (editorInterface.isEmpty && editorInterface.getText().length === 0) ||
        (editorInterface.getText().match(/^\s+$/) !== null &&
          editorInterface.getText() !== editorMarkupForEmptyLists);

      setIsEditorEmpty?.(isEmpty);

      let content: string;
      if (usageRef.current === 'commentExternal') {
        content = editorInterface.getText();
      } else {
        // If the user typed nothing, no need to send a JSON with no content
        content = isEmpty ? '' : JSON.stringify(editorInterface.getJSON());
      }

      onChangeText(content);

      /**
       * Validate programmatically,
       * because native maxLength attribute doesn't work with controlled components,
       * instead checkValidity always returns true, as the initial value was over the limit
       */
      validateTextareaMaxLength(textareaRef, content, usage);
      // Trigger native onChange event, so that any listeners above can catch it as if this was a regular textarea
      const event = new Event('input', { bubbles: true });
      textareaRef.current?.dispatchEvent(event);
    },
  });

  // Strip formatting when switching to guest tab (external comment)
  useEffect(() => {
    usageRef.current = usage;

    if (usage === 'commentExternal') {
      const content = editor?.getText() ?? '';

      editor?.commands.setContent(content);
      onChangeText(content);
    }
    /**
     * @todo Uncomment this
     *
     * It caused a problem on mobile, navigating
     * home ➞ topic ➞ post ➞ edit (title & description)
     * would focus the editor before the animation ended, making it jump
     */
    // editor.commands.focus('end');

    // Only trigger this effect when usage prop changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usage]);

  // Clear editor text when it's cleared in props
  useEffect(() => {
    if (value.length === 0) {
      editor?.commands.clearContent();
    }

    // This fixes old bug when the value is updated programmatically
    if (value !== '' && editor.isEmpty) {
      editor?.commands.setContent(parseText(value));
      onChangeText(value);
    }

    // editor?.comments in dependencies causes a memory leak
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // User switched to editing a different node
  useEffect(() => {
    // To prevent posting formatted text for external comments
    if (usage !== 'commentExternal') {
      editor?.commands.setContent(parseText(value));
    }
    // We DON'T want to apply this on every Redux value change
    // That messes up cursor positioning
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor === null, nodeId]);

  if (editor === null) {
    return null;
  }

  /**
   * Bring focus to the editor (used when the styling tools are toggle on mobile)
   */
  const focusEditor = () => {
    editor.commands.focus('end');
  };

  if (multiline === false) {
    return (
      <Styled.Input
        id={id}
        isDisabled={isDisabled}
        isRequired={isRequired}
        maxLength={maxLength}
        minLength={minLength}
        name={name}
        onBlur={onBlur}
        onChange={onChangeText}
        shouldFocus={shouldFocus}
        value={value}
      />
    );
  }

  return (
    <Styled.Wrapper
      className={className}
      data-invalid={isInvalid || getIsMaxLengthExceeded(textareaRef.current)}
    >
      <Styled.Editor
        data-breakpoint-min={breakpointMin}
        data-editor-usage={usage}
        editor={editor}
        onBlur={onBlur}
      />
      {/* Plain textarea that's invisible for the users in order to support <label> click */}
      {/* and native form validation for required, minlength, maxlength */}
      <Styled.Textarea
        aria-hidden={true}
        aria-invalid={isInvalid}
        autoFocus={shouldFocus}
        defaultValue={editor.isEmpty ? '' : value}
        disabled={isDisabled}
        id={id}
        maxLength={maxLength}
        minLength={minLength}
        name={name}
        onFocus={focusEditor}
        placeholder={placeholder}
        ref={textareaRef}
        required={isRequired}
        // Tabbing would trap focus on the editor, we want element that can be focused (by clicking on <label>)
        // but is not available for sequential focus via tab key
        tabIndex={-1}
      />
      {showControls && (
        <EditorControls
          data-breakpoint-min={breakpointMin}
          editor={editor}
          requestEditCancel={requestEditCancel}
          shouldDisableSubmit={shouldDisableSubmit}
          usage={usage}
        />
      )}
    </Styled.Wrapper>
  );
};

export default Editor;
